import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccessCheckoutForm,
  AccessCheckoutFormSchema,
} from 'api/accessexpressv3/schemas/AccessCheckoutSchemas';
import { useMutation } from 'react-query';
import { submitOrder, updateOrder } from 'api/accessexpressv3/api';
import showToast from 'components/Toast/Toast';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { CartSiteAddress, useCartContext } from '../CartProvider';

interface AccessCheckoutContextType {
  currentIndex: number;
  locations: CartSiteAddress[];
  form: UseFormReturn<AccessCheckoutForm>;
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  isCheckoutFormValid: () => Promise<boolean>;
  onSubmit: (data: AccessCheckoutForm) => Promise<void>;
  isCheckoutLoading: boolean;
}

const AccessCheckoutContext = createContext<AccessCheckoutContextType | null>(
  null,
);

export const AccessCheckoutProvider = observer(
  ({ children }: { children: React.ReactNode }) => {
    const cart = useCartContext();
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState(0);

    const form = useForm<AccessCheckoutForm>({
      resolver: zodResolver(AccessCheckoutFormSchema),
      defaultValues: {},
    });

    useEffect(() => {
      if (!cart.loadingDraftForPricing) {
        form.reset({
          locations: cart.locations.map((loc) => ({
            address_line_1: loc.address_line_1,
            id: loc.id as string,
            macnum: loc.macnum,
            city: loc.city,
            state: loc.state,
            street: loc.street,
            zip: loc.zip,
          })),
        });
      }
    }, [cart.loadingDraftForPricing, cart.locations, form]);

    const { trigger } = form;

    const totalLocations = cart.locations.length;

    const goToPrevious = useCallback(() => {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }, []);
    const goToNext = useCallback(async () => {
      const isValid = await trigger(`locations.${currentIndex}`);
      // show errors also on first time! not the right way but this works for now!
      const [
        currentMacnum,
        currentBillingType,
        currentInvoiceStyle,
        currentInvoiceDeliveryMethod,
        currentParentAccount,
        currentLocationName,
      ] = form.getValues([
        `locations.${currentIndex}.macnum`,
        `locations.${currentIndex}.billing_type`,
        `locations.${currentIndex}.invoice_style`,
        `locations.${currentIndex}.invoice_delivery_method`,
        `locations.${currentIndex}.parent_account`,
        `locations.${currentIndex}.location_name`,
      ]);
      if (!currentMacnum) {
        if (!currentBillingType?.value) {
          form.setError(`locations.${currentIndex}.billing_type`, {
            message: 'Required',
          });
        }
        if (!currentInvoiceStyle?.value) {
          form.setError(`locations.${currentIndex}.invoice_style`, {
            message: 'Required',
          });
        }
        if (!currentInvoiceDeliveryMethod?.value) {
          form.setError(`locations.${currentIndex}.invoice_delivery_method`, {
            message: 'Required',
          });
        }
        if (!currentParentAccount?.value) {
          form.setError(`locations.${currentIndex}.parent_account`, {
            message: 'Required',
          });
        }
        if (!currentLocationName?.trim()) {
          form.setError(`locations.${currentIndex}.location_name`, {
            message: 'Required',
          });
        }
      }
      if (!isValid) return;

      setCurrentIndex((prev) => Math.min(prev + 1, totalLocations - 1));
    }, [trigger, currentIndex, totalLocations, form]);

    const { mutate, isLoading } = useMutation(
      ({ draftId, order_name }: { draftId: string; order_name: string }) =>
        submitOrder(draftId, { order_name }),
      {
        onSuccess: () => {
          showToast.confirmation({
            message: 'New order placed successfully!',
          });
          cart.reset();
          navigate('/access-express/v3/order-services/access');
        },
      },
    );

    const onSubmit = async (data: AccessCheckoutForm) => {
      const _locations = cart.locations.map((location, idx) => ({
        address: {
          ...location,
          ...data.locations[idx],
          ...(!location.macnum
            ? {
                parent_macnum: data.locations[idx].parent_account?.value,
                parent_name: data.locations[idx].parent_account?.label,
              }
            : {}),
          billing_type: data.locations[idx].billing_type?.value ?? null,
          invoice_style: data.locations[idx].invoice_style?.value ?? null,
          invoice_delivery_method:
            data.locations[idx].invoice_delivery_method?.value ?? null,
          id: location.id as string,
        },
        order_items: cart.itemsByLocation(location.id).map((item) => ({
          type: item.category ?? '',
          subtype: item.subCategory ?? '',
          // need to handle
          pim_price_id: null,
          item_details: item.item_details ?? [],
          price: item.price ?? 0,
          quantity: item.quantity ?? 0,
          location_id: String(item.locationId ?? ''),
        })),
      }));
      if (cart.draftId) {
        await updateOrder(
          {
            additional_details: cart.additionalDetails ?? {},
            locations: _locations,
            order_name: data.order_name as string,
          },
          cart.draftId,
        );
        mutate({
          draftId: cart.draftId,
          order_name: data.order_name as string,
        });
      }
    };

    const isCheckoutFormValid = useCallback(async () => {
      // show errors also on first time! not the right way but this works for now!
      const isValid = await Promise.all(
        cart.locations.map(async (_, index) => {
          const valid = await trigger(`locations.${index}`);
          const [
            currentMacnum,
            currentBillingType,
            currentInvoiceStyle,
            currentInvoiceDeliveryMethod,
            currentParentAccount,
            currentLocationName,
          ] = form.getValues([
            `locations.${index}.macnum`,
            `locations.${index}.billing_type`,
            `locations.${index}.invoice_style`,
            `locations.${index}.invoice_delivery_method`,
            `locations.${index}.parent_account`,
            `locations.${index}.location_name`,
          ]);
          if (!currentMacnum) {
            if (!currentBillingType?.value) {
              form.setError(`locations.${index}.billing_type`, {
                message: 'Required',
              });
            }
            if (!currentInvoiceStyle?.value) {
              form.setError(`locations.${index}.invoice_style`, {
                message: 'Required',
              });
            }
            if (!currentInvoiceDeliveryMethod?.value) {
              form.setError(`locations.${index}.invoice_delivery_method`, {
                message: 'Required',
              });
            }
            if (!currentParentAccount?.value) {
              form.setError(`locations.${index}.parent_account`, {
                message: 'Required',
              });
            }
            if (!currentLocationName?.trim()) {
              form.setError(`locations.${index}.location_name`, {
                message: 'Required',
              });
            }
          }
          return valid;
        }),
      ).then((results) => results.every(Boolean));

      return isValid;
    }, [cart.locations, trigger, form]);

    return (
      <AccessCheckoutContext.Provider
        value={{
          form,
          locations: cart.locations,
          goToNext,
          goToPrevious,
          currentIndex,
          onSubmit,
          isCheckoutFormValid,
          isCheckoutLoading: isLoading,
        }}
      >
        {children}
      </AccessCheckoutContext.Provider>
    );
  },
);

export const useAccessCheckoutContext = () => {
  const context = useContext(AccessCheckoutContext);
  if (!context) {
    throw new Error(
      'useAccessCheckoutContext must be used within a CheckoutProvider',
    );
  }
  return context;
};
